import { MIN_RATE_N, ratesDiffer, wilson, } from './game-events.js';
/**
 * Game event reports in words (`docs/events-ux-plan.md`).
 *
 * The reports carry rates with intervals and percentiles, which is what the maths needs and not what
 * a developer reads. Everything a person is shown about a report goes through this module, so the
 * dashboard and the headline an agent reads say the same thing the same way:
 *
 *  - **Names, not identifiers.** `first_win` is "First win" unless the catalog gave a label.
 *  - **People and tries, said out loud.** "17 of 25 players", "63% of tries", never a bare 63%
 *    whose denominator the reader has to guess.
 *  - **Typical, not median; "most between", not p10–p90.** A median is "typical", and the middle
 *    eight in ten is "most".
 *  - **A difference is only claimed where the intervals do not overlap** (game-events-plan §3.3),
 *    and below `MIN_RATE_N` a rate is "too early to tell" with the count it has so far. The
 *    interval is never printed as a pair of numbers in a sentence; a chart shows it as a band.
 *  - **Nobody is not nobody.** A table's event (`who: 'table'`, sent without a player by a game that
 *    names players elsewhere) is said in tries, picks and sessions — never as "nobody has played",
 *    which is only ever said of an event nothing arrived for.
 */
/* ------------------------------------------------------------------------------------ names */
/** An event's name as a person reads it: the catalog's label, or `first_win` as "First win". */
export function eventTitle(name, label) {
    if (label && label !== name)
        return label;
    const words = name.replace(/_+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/**
 * A progress unit as a person reads it. A unit the catalog labelled is its label ("Meadow"); a bare
 * number-like id is prefixed with the event's own name ("Level 1-1"), because "1-1" alone is a
 * coordinate, not a place.
 */
export function unitTitle(eventLabel, unit) {
    if (unit.label !== unit.unit)
        return unit.label;
    return /^[0-9][0-9.\-_:]*$/.test(unit.unit) ? `${eventLabel} ${unit.unit}` : unit.unit;
}
const PHASE_WORDS = {
    start: 'started',
    complete: 'cleared',
    fail: 'failed',
    quit: 'quit',
};
/**
 * A catalog funnel step (`level:1-1:complete`, `tutorial_done`) in words: "Level 1-1 cleared",
 * "Tutorial done". `titles` maps event names to their labels.
 */
export function funnelStepTitle(step, titles = {}) {
    const [event = step, subject, phase] = step.split(':');
    const title = titles[event] ?? eventTitle(event);
    return [title, subject, phase === undefined ? undefined : (PHASE_WORDS[phase] ?? phase)].filter(Boolean).join(' ');
}
/* ---------------------------------------------------------------------------------- numbers */
/**
 * A word's plural, by the three rules that cover the nouns this module counts — tries, matches,
 * players, levels — and the last word of a label: "Versus match" → "Versus matches".
 */
export function pluralWord(word) {
    if (/(s|x|z|ch|sh)$/i.test(word))
        return `${word}es`;
    if (/[^aeiou]y$/i.test(word))
        return `${word.slice(0, -1)}ies`;
    return `${word}s`;
}
/** `1 player`, `3 players`, `2 matches`. */
export function count(n, noun) {
    return `${n.toLocaleString('en-US')} ${n === 1 ? noun : pluralWord(noun)}`;
}
/** A share as a whole percentage. */
export function percent(share) {
    return `${Math.round(share * 100)}%`;
}
/** Whether a rate has enough behind it to be said as a percentage (§3.3). */
export function enough(rate) {
    return rate.rate !== null && rate.n >= MIN_RATE_N;
}
/**
 * A rate with its denominator in words: `63% of 64 tries`, or below the threshold
 * `5 of 8 matches so far`. The noun is the thing counted in the denominator.
 */
export function rateWords(rate, noun) {
    if (rate.rate === null || rate.n === 0)
        return `no ${pluralWord(noun)} yet`;
    if (!enough(rate))
        return `${rate.k} of ${count(rate.n, noun)} so far`;
    return `${percent(rate.rate)} of ${count(rate.n, noun)}`;
}
/** What "too early to tell" is waiting for: `8 matches so far, 10 needed`. */
export function tooEarly(rate, noun) {
    return `${count(rate.n, noun)} so far, ${MIN_RATE_N} needed`;
}
/**
 * The likely range of a rate, for a tooltip: "likely between 45% and 78%". Never in a headline: a
 * pair of numbers in a sentence reads as two facts.
 */
export function likelyRange(rate) {
    if (rate.low === null || rate.high === null || !enough(rate))
        return null;
    return `likely between ${percent(rate.low)} and ${percent(rate.high)}`;
}
/** Milliseconds as a person says a length of time: `58 s`, `3 min 42 s`, `1 h 5 min`. */
export function timeWords(ms) {
    if (ms === null)
        return '—';
    if (ms < 1000)
        return 'under a second';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60)
        return `${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 10)
        return seconds % 60 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`;
    if (minutes < 60)
        return `${Math.round(seconds / 60)} min`;
    const hours = Math.floor(minutes / 60);
    return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
}
/** The typical value — the median, which is the word this module never prints. */
export function typical(p, format = String) {
    return p.p50 === null ? null : format(p.p50);
}
/** Where most values fall — the middle eight in ten, p10 to p90 — as `11–39`. */
export function mostBetween(p, format = String, joiner = '–') {
    if (p.p10 === null || p.p90 === null)
        return null;
    return p.p10 === p.p90 ? format(p.p10) : `${format(p.p10)}${joiner}${format(p.p90)}`;
}
export function standings(rates) {
    return rates.map((rate, i) => {
        if (!enough(rate))
            return 'few';
        let above = false;
        let below = false;
        rates.forEach((other, j) => {
            if (i === j || !ratesDiffer(rate, other))
                return;
            if (rate.rate > other.rate)
                above = true;
            else
                below = true;
        });
        if (above && !below)
            return 'higher';
        if (below && !above)
            return 'lower';
        return 'level';
    });
}
/** The words a standing shows as, beside its number. */
export const STANDING_WORDS = {
    higher: 'Clearly higher',
    lower: 'Clearly lower',
    level: '',
    few: 'Too early to tell',
};
/**
 * One sentence over a comparison: which option clearly does better than which, or that none does
 * yet, or that it is too early. `better` sits between two options: `'wins more often than'`.
 */
export function compareSentence(rows, words) {
    const ranks = standings(rows.map((row) => row.rate));
    const ranked = rows.map((row, i) => ({ ...row, standing: ranks[i] }));
    const counted = ranked.filter((row) => row.standing !== 'few');
    if (counted.length < 2) {
        return `Too early to tell: each ${words.thing} needs at least ${count(MIN_RATE_N, words.noun)}.`;
    }
    const best = ranked.filter((row) => row.standing === 'higher').sort((a, b) => b.rate.rate - a.rate.rate)[0];
    const worst = ranked.filter((row) => row.standing === 'lower').sort((a, b) => a.rate.rate - b.rate.rate)[0];
    if (best && worst) {
        return `${best.label} ${words.better} ${worst.label}: ${percent(best.rate.rate)} against ${percent(worst.rate.rate)}.`;
    }
    if (best || worst) {
        // Only one end is clear: say that one against the field.
        const row = (best ?? worst);
        return `${row.label} ${best ? words.better : 'does worse than'} the others: ${percent(row.rate.rate)}.`;
    }
    // Nothing clears another's interval. When the gap a reader can see is big, say so, and say why
    // it is not a finding yet — "no difference" beside 75% and 25% reads as a bug.
    const shown = [...counted].sort((a, b) => b.rate.rate - a.rate.rate);
    const top = shown[0];
    const bottom = shown[shown.length - 1];
    if (top.rate.rate - bottom.rate.rate >= 0.1) {
        return `${top.label} is ahead, ${percent(top.rate.rate)} against ${percent(bottom.rate.rate)}, but with this few ${pluralWord(words.noun)} that could still be chance.`;
    }
    return `No ${words.thing} clearly does better or worse yet.`;
}
/**
 * The words a choice's balance against one outcome or progress event compares in. A balance entry
 * says which by its `measure`: a win rate counts seats in matches, anything else counts tries.
 */
export function balanceWords(choiceLabel, entry, titles = {}) {
    const target = titles[entry.event] ?? eventTitle(entry.event, entry.label);
    const thing = choiceLabel.toLowerCase();
    if (/^win/.test(entry.measure)) {
        // "Matches won", counting the seats a pick played: one match is a try at winning for each seat.
        const words = target.split(' ');
        const last = words.pop();
        return { thing, better: 'wins more often than', noun: last.toLowerCase(), column: `${[...words, pluralWord(last)].join(' ')} won` };
    }
    return { thing, better: `clears more ${target.toLowerCase()} tries than`, noun: 'try', column: `${target} tries cleared` };
}
/**
 * The progress ladder as steps a person reads: who got through each unit, and who stopped there.
 *
 * A table's ladder (`who: 'table'`) has nobody to follow from one unit to the next, so each step is
 * that unit's tries instead: `cleared` is the tries that ended cleared, of the tries that ended, and
 * `lost` the ones that did not.
 */
export function ladderSteps(label, body, who) {
    if (who === 'table') {
        return body.units.map((unit) => ({
            unit: unit.unit,
            title: unitTitle(label, unit),
            cleared: unit.completion,
            lost: unit.completion.n - unit.completion.k,
            spike: unit.spike,
        }));
    }
    return body.units.map((unit, i) => {
        const before = i === 0 ? unit.reach.n : body.units[i - 1].reach.k;
        return {
            unit: unit.unit,
            title: unitTitle(label, unit),
            cleared: unit.reach,
            lost: Math.max(0, before - unit.reach.k),
            spike: unit.spike,
        };
    });
}
/** The step most players stopped at, when there is one worth naming. */
export function biggestDrop(steps) {
    const players = steps[0]?.cleared.n ?? 0;
    if (players < MIN_RATE_N)
        return null;
    const sorted = [...steps].sort((a, b) => b.lost - a.lost);
    return sorted[0] && sorted[0].lost > 0 ? sorted[0] : null;
}
/** "levels", from "Level" — a plural only for a one-word label, which is what a unit name is. */
function unitsNoun(label) {
    return /^[A-Za-z]+$/.test(label) ? pluralWord(label.toLowerCase()) : null;
}
/* ---------------------------------------------------------------------------------- funnels */
/**
 * Why a funnel cannot be followed, when it cannot: the steps a table sends (`who: 'table'`), which
 * nobody can be followed through. Null for a funnel that follows its players all the way.
 */
export function funnelBreak(steps, titles = {}) {
    const table = steps.filter((step) => step.who === 'table').map((step) => funnelStepTitle(step.step, titles));
    if (!table.length)
        return null;
    const list = table.length === 1 ? table[0] : `${table.slice(0, -1).join(', ')} and ${table[table.length - 1]}`;
    return `This funnel can’t follow anybody through it: ${list} ${table.length === 1 ? 'is' : 'are'} sent without a player.`;
}
/** What a funnel that cannot be followed shows instead, said under it after `funnelBreak`. */
export const FUNNEL_COUNTS_NOTE = 'The bars show how often each step happened, in any order.';
/** One step of such a funnel: `13 times`, or `13 times · 9 players` for a step that names its players. */
export function funnelStepCount(step) {
    const times = count(step.times ?? 0, 'time');
    return step.players ? `${times} · ${count(step.players, 'player')}` : times;
}
/**
 * Who the overview's player count is, when some events are the table's: only the players named on
 * the events that name one — `only those named on Tutorial done and Campaign won` — since a player
 * who only ever played through the table's events is in none of them. Null when every event that
 * arrived names its players, or none does.
 */
export function namedPlayersNote(cards) {
    if (!cards.some((card) => card.who === 'table' && card.count > 0))
        return null;
    const named = cards.filter((card) => card.who === 'player' && card.players > 0).map((card) => eventTitle(card.name, card.label));
    if (!named.length)
        return null;
    const list = named.length <= 3 ? (named.length === 1 ? named[0] : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`) : count(named.length, 'event');
    return `only those named on ${list}`;
}
/* --------------------------------------------------------------------------- a table's words */
/** A table's ladder in one sentence: how far a clear has got, and where tries most often fall short. */
function tableProgressHeadline(label, body) {
    const steps = ladderSteps(label, body, 'table');
    const tries = body.units.reduce((sum, unit) => sum + unit.starts, 0);
    const furthest = [...steps].reverse().find((step) => step.cleared.k > 0);
    if (!furthest)
        return tries ? `${count(tries, 'try')}, none cleared yet` : 'Not played yet';
    const reach = furthest === steps[steps.length - 1] ? `${furthest.title} cleared ${count(furthest.cleared.k, 'time')}` : `Cleared as far as ${furthest.title}`;
    const short = steps.reduce((sum, step) => sum + step.lost, 0);
    const worst = [...steps].sort((a, b) => b.lost - a.lost)[0];
    return short >= MIN_RATE_N && worst && worst.lost > 1 ? `${reach}; most tries that fall short are at ${worst.title}` : reach;
}
/** A table's choice in one sentence: a share of picks, since nobody picked it. */
function tableChoiceHeadline(body) {
    const total = body.options.reduce((sum, option) => sum + option.picks, 0);
    const picked = [...body.options].filter((option) => option.picks > 0).sort((a, b) => b.picks - a.picks);
    if (!picked.length)
        return 'Nothing picked yet';
    const sure = enough(wilson(picked[0].picks, total));
    const amount = (picks) => (sure ? percent(picks / total) : count(picks, 'time'));
    const [first, ...rest] = picked.slice(0, 3);
    const others = rest.map((option) => `, ${option.label} ${amount(option.picks)}`).join('');
    const more = picked.length > 3 ? `, and ${picked.length - 3} more` : '';
    return sure
        ? `${first.label} is ${amount(first.picks)} of picks${others}${more}`
        : `So far, ${first.label} is picked ${amount(first.picks)}${others}${more}`;
}
/* -------------------------------------------------------------------------------- headlines */
/**
 * An event's report in one sentence (§2.1's headline), for the list of events and for an agent
 * reading the overview. Counts where a percentage would claim too much, and a table's event in
 * what it counted rather than in people.
 */
export function eventHeadline(report) {
    const body = report.body;
    const label = eventTitle(report.name, report.label);
    const table = report.who === 'table';
    switch (body.kind) {
        case 'progress': {
            if (table)
                return tableProgressHeadline(label, body);
            const steps = ladderSteps(label, body);
            const last = steps[steps.length - 1];
            if (!last || last.cleared.n === 0)
                return 'Nobody has played it yet';
            const noun = unitsNoun(label);
            const all = steps.length === 1 ? last.title : noun ? `all ${steps.length} ${noun}` : `through ${last.title}`;
            const drop = biggestDrop(steps);
            const stop = drop ? `; most who stop, stop at ${drop.title}` : '';
            if (last.cleared.k === 0)
                return `Nobody has cleared ${all} yet${stop}`;
            const share = enough(last.cleared) ? `${percent(last.cleared.rate)} of players` : `${last.cleared.k} of ${count(last.cleared.n, 'player')}`;
            return `${share} cleared ${all}${stop}`;
        }
        case 'choice': {
            if (table)
                return tableChoiceHeadline(body);
            // Per player, as the report's share is (§3.2): one player picking three times is one player.
            const picked = [...body.options].filter((option) => option.picks > 0).sort((a, b) => b.share.k - a.share.k);
            if (!picked.length)
                return 'Nothing picked yet';
            const sure = enough(picked[0].share);
            const amount = (option, first) => sure ? `${percent(option.share.rate)}${first ? ' of players' : ''}` : first ? count(option.share.k, 'player') : String(option.share.k);
            const [first, ...rest] = picked.slice(0, 3);
            const others = rest.map((option) => `, ${option.label} by ${amount(option, false)}`).join('');
            const more = picked.length > 3 ? `, and ${picked.length - 3} more` : '';
            return `${sure ? '' : 'So far, '}${first.label} is picked by ${amount(first, true)}${others}${more}`;
        }
        case 'outcome': {
            if (!body.matches)
                return 'No matches yet';
            const length = typical(body.length, timeWords);
            return `${count(body.matches, 'match')}${length ? `, typically ${length} long` : ''}`;
        }
        case 'value': {
            if (!body.values.n)
                return 'Nothing recorded yet';
            const unit = body.unit === 'count' ? '' : ` ${body.unit}`;
            const spread = body.values.p10 !== body.values.p90 ? mostBetween(body.values, String, ' and ') : null;
            return `Typically ${body.values.p50}${unit}${spread ? `; most between ${spread}` : ''}`;
        }
        case 'milestone':
            if (table) {
                // Nobody reaches a table's milestone; a session does.
                const sessions = body.sessions;
                if (!sessions?.n)
                    return 'Sent for the table, not for a player';
                return enough(sessions)
                    ? `Reached in ${percent(sessions.rate)} of sessions (${sessions.k} of ${sessions.n})`
                    : `Reached in ${sessions.k} of ${count(sessions.n, 'session')} so far`;
            }
            if (body.reach.n === 0)
                return 'Nobody has played yet';
            return enough(body.reach)
                ? `${percent(body.reach.rate)} of players reached it (${body.reach.k} of ${body.reach.n})`
                : `${body.reach.k} of ${count(body.reach.n, 'player')} reached it so far`;
        case 'action':
            if (!body.count)
                return 'Not used yet';
            return `${count(body.count, 'time')}, in ${body.sessionsUsing.k} of ${count(body.sessionsUsing.n, 'session')}`;
        case 'economy':
            return `${body.sources.toLocaleString('en-US')} ${body.currency} earned, ${Math.abs(body.sinks).toLocaleString('en-US')} spent`;
        case 'undeclared':
            return `${count(body.count, 'event')} received, not in the catalog`;
    }
}
/** The overview's "What stands out": a few sentences, each only where the numbers support it. */
export function eventHighlights(input) {
    const out = [];
    const titles = Object.fromEntries(input.reports.map((report) => [report.name, eventTitle(report.name, report.label)]));
    for (const report of input.reports) {
        const body = report.body;
        const label = titles[report.name];
        // Who stopped where is about people; a table's ladder has none to have stopped.
        if (body.kind === 'progress' && report.who !== 'table') {
            const steps = ladderSteps(label, body);
            const spike = body.units.findIndex((unit) => unit.spike);
            if (spike > 0) {
                const unit = body.units[spike];
                const before = body.units[spike - 1];
                out.push({
                    tone: 'problem',
                    event: report.name,
                    text: `${steps[spike].title} is much harder than the one before: ${percent(unit.completion.rate)} of tries succeed, down from ${percent(before.completion.rate)}.`,
                });
            }
            const drop = biggestDrop(steps);
            if (drop && drop.lost >= 2 && (spike < 0 || steps[spike].unit !== drop.unit)) {
                out.push({
                    tone: 'problem',
                    event: report.name,
                    text: `${count(drop.lost, 'player')} got as far as ${drop.title} and stopped there.`,
                });
            }
        }
        if (body.kind === 'choice') {
            for (const entry of body.balance) {
                const rows = entry.options.map((option) => ({ label: option.label, rate: option.rate }));
                const ranks = standings(rows.map((row) => row.rate));
                if (!ranks.includes('higher') && !ranks.includes('lower'))
                    continue;
                out.push({ tone: 'finding', event: report.name, text: compareSentence(rows, balanceWords(label, entry, titles)) });
            }
        }
        if (body.kind === 'outcome') {
            const ranks = standings(body.bySeat.map((row) => row.rate));
            const best = body.bySeat.findIndex((_, i) => ranks[i] === 'higher');
            if (best >= 0) {
                out.push({
                    tone: 'finding',
                    event: report.name,
                    text: `Seat ${Number(body.bySeat[best].group) + 1} wins more often than the others: ${percent(body.bySeat[best].rate.rate)} of ${count(body.matches, 'match')}.`,
                });
            }
        }
        if (body.kind === 'economy' && body.sources > 0 && body.sinks === 0) {
            out.push({ tone: 'finding', event: report.name, text: `Players earn ${body.currency} but have not spent any.` });
        }
    }
    if (input.undeclared.length) {
        const names = input.undeclared.slice(0, 3).join(', ');
        out.push({
            tone: 'todo',
            event: null,
            text: `${count(input.undeclared.length, 'event')} arrived that ${input.undeclared.length === 1 ? 'is' : 'are'} not in the catalog yet: ${names}${input.undeclared.length > 3 ? '…' : ''}.`,
        });
    }
    const order = { problem: 0, finding: 1, todo: 2 };
    return out.sort((a, b) => order[a.tone] - order[b.tone]).slice(0, 5);
}
//# sourceMappingURL=game-event-words.js.map